import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayout } from '@client/components/_core/layout/PageLayout';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { DatePicker } from '@client/components/_ui/data-entry/DatePicker';
import { Markdown } from '@client/components/_ui/display/Markdown';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { PdsAnalysisTab } from '@client/components/market-pds/PdsAnalysisTab';
import { SmartPdsUploadModal } from '@client/components/market-pds/SmartPdsUploadModal';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterOutputs } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { uploadToS3 } from '@client/utils/common/s3-upload';
import { createFileRoute } from '@tanstack/react-router';
import {
    Alert,
    Card,
    Descriptions,
    Empty,
    Form,
    Image,
    Input,
    InputNumber,
    Popconfirm,
    Select,
    Skeleton,
    Table,
    Tabs,
    Tag,
    Upload,
} from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { FileText, RefreshCw, Replace, Trash2, Upload as UploadIcon, X } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { MARKET_PDS_STATUS_COLORS } from './status-colors';

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/admin-console/platform-content/market-pds/edit/$id')({
    params: {
        parse({ id }) {
            return { id };
        },
    },
    staticData: {
        presentation: 'fullPage',
        ui: {
            icon: FileText,
            widthVariant: 'default',
        },
    },
    beforeLoad({ context: { trpcUtils }, params }): ShareableRouteContext {
        async function title() {
            const pds = await trpcUtils.admin.marketPds.getMarketPds.ensureData({ id: BigInt(params.id) });
            const name = pds?.name || 'Edit PDS';
            return (
                <span className="inline-block max-w-md truncate align-bottom" title={name}>
                    {name}
                </span>
            );
        }
        return {
            breadcrumb: { title: 'Edit PDS' },
            ui: { title, subtitle: 'View and edit PDS details' },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <EditMarketPds />
        </PageController>
    );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MarketPdsData = RouterOutputs['admin']['marketPds']['getMarketPds'];
type MarketPdsSection = MarketPdsData['Sections'][number];

type EditFormValues = {
    name: string;
    insurerName: string;
    insurerLogo: string | null;
    insurerWebsite: string | null;
    category: string;
    year: number;
    version: string | null;
    effectiveDate: Date | null;
    status: 'DRAFT' | 'PROCESSING' | 'PUBLISHED' | 'ARCHIVED';
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Processing', value: 'PROCESSING' },
    { label: 'Published', value: 'PUBLISHED' },
    { label: 'Archived', value: 'ARCHIVED' },
];

const SECTION_TYPE_COLORS: Record<string, string> = {
    COVERAGE: 'green',
    EXCLUSION: 'red',
    CONDITION: 'blue',
    LIMIT: 'purple',
    DEFINITION: 'cyan',
    PROCEDURE: 'orange',
    GENERAL: 'default',
};

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
const MAX_LOGO_SIZE = 5 * 1024 * 1024; // 5 MB

// ---------------------------------------------------------------------------
// Logo field component
// ---------------------------------------------------------------------------

function InsurerLogoField({
    logoUrl,
    isReadOnly,
    isUploading,
    onUpload,
    onRemove,
}: {
    logoUrl: string | null;
    isReadOnly: boolean;
    isUploading: boolean;
    onUpload: (file: RcFile) => void;
    onRemove: () => void;
}) {
    if (logoUrl) {
        return (
            <div className="flex items-center gap-4">
                <Image
                    src={logoUrl}
                    alt="Insurer logo"
                    height={48}
                    preview={{ mask: 'Preview' }}
                    className="rounded border border-separator bg-card object-contain p-1"
                    style={{ maxWidth: 160 }}
                    fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIGZpbGw9IiNmM2Y0ZjYiIHJ4PSI0Ii8+PHRleHQgeD0iMjQiIHk9IjI4IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOWNhM2FmIiBmb250LXNpemU9IjEyIj4/PC90ZXh0Pjwvc3ZnPg=="
                />
                {!isReadOnly && (
                    <Space size="small">
                        <Upload
                            showUploadList={false}
                            beforeUpload={(file) => {
                                onUpload(file);
                                return false;
                            }}
                            accept=".png,.jpg,.jpeg,.svg,.webp"
                        >
                            <Button type="secondary" size="small" icon={<Replace />} loading={isUploading}>
                                Replace
                            </Button>
                        </Upload>
                        <Button type="danger" size="small" icon={<Trash2 />} onClick={onRemove}>
                            Remove
                        </Button>
                    </Space>
                )}
            </div>
        );
    }

    if (isReadOnly) {
        return <span className="text-muted-foreground text-sm">No logo</span>;
    }

    return (
        <Upload
            showUploadList={false}
            beforeUpload={(file) => {
                onUpload(file);
                return false;
            }}
            accept=".png,.jpg,.jpeg,.svg,.webp"
        >
            <Button type="secondary" size="small" icon={<UploadIcon />} loading={isUploading}>
                Upload logo
            </Button>
        </Upload>
    );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function EditMarketPds() {
    const { id } = Route.useParams();
    const { trpc, trpcUtils, trpcClient } = useTrpc();
    const { formatDateTime } = useFormatter();
    const { isReadOnly } = useAdminModulePermissions('comms');
    const formId = useId();
    const [form] = Form.useForm<EditFormValues>();
    const [replaceFileOpen, setReplaceFileOpen] = useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);

    const pdsId = BigInt(id);

    const {
        data: marketPds,
        isLoading,
        error,
    } = trpc.admin.marketPds.getMarketPds.useQuery(
        { id: pdsId },
        {
            refetchOnWindowFocus: false,
            refetchInterval: (query) => {
                const status = query.state.data?.status;
                return status === 'PROCESSING' ? 5000 : false;
            },
        },
    );

    const { data: categories } = trpc.admin.marketPds.listCategories.useQuery();
    const { data: insurers } = trpc.admin.marketPds.listInsurers.useQuery();

    const { mutate: updateMarketPds, isPending: isSaving } = trpc.admin.marketPds.updateMarketPds.useMutation({
        onError: (err) => errorMessage.show(err),
    });

    const { mutate: reprocessMutate } = trpc.admin.marketPds.reprocessMarketPds.useMutation({
        onError: (err) => errorMessage.show(err),
    });

    const { mutate: cancelProcessingMutate, isPending: isCancelling } =
        trpc.admin.marketPds.cancelProcessing.useMutation({
            onError: (err) => errorMessage.show(err),
        });

    // Populate form when data loads
    useEffect(() => {
        if (!marketPds) return;
        form.setFieldsValue({
            name: marketPds.name,
            insurerName: marketPds.insurerName,
            insurerLogo: marketPds.insurerLogo,
            insurerWebsite: marketPds.insurerWebsite,
            category: marketPds.category,
            year: marketPds.year,
            version: marketPds.version,
            effectiveDate: marketPds.effectiveDate,
            status: marketPds.status,
        });
    }, [marketPds?.id, form]);

    const handleSave = (values: EditFormValues) => {
        const key = 'update_pds';
        message.loading({ content: 'Saving...', key });
        updateMarketPds(
            {
                id: pdsId,
                set: {
                    name: values.name,
                    insurerName: values.insurerName,
                    insurerLogo: values.insurerLogo ?? null,
                    insurerWebsite: values.insurerWebsite ?? null,
                    category: values.category,
                    year: values.year,
                    version: values.version ?? null,
                    effectiveDate: values.effectiveDate ?? null,
                    status: values.status,
                },
            },
            {
                onSuccess: () => {
                    message.success({ content: 'PDS updated', key });
                    trpcUtils.admin.marketPds.getMarketPds.invalidate({ id: pdsId });
                    trpcUtils.admin.marketPds.listMarketPds.invalidate();
                    trpcUtils.admin.marketPds.listCategories.invalidate();
                    trpcUtils.admin.marketPds.listInsurers.invalidate();
                },
            },
        );
    };

    const handleReprocess = () => {
        const key = 'reprocess';
        message.loading({ content: 'Queuing reprocessing...', key });
        reprocessMutate(
            { id: pdsId },
            {
                onSuccess: () => {
                    message.success({ content: 'Reprocessing queued', key });
                    trpcUtils.admin.marketPds.getMarketPds.invalidate({ id: pdsId });
                    trpcUtils.admin.marketPds.listMarketPds.invalidate();
                },
            },
        );
    };

    const handleCancelProcessing = () => {
        const key = 'cancel_processing';
        message.loading({ content: 'Cancelling...', key });
        cancelProcessingMutate(
            { id: pdsId },
            {
                onSuccess: () => {
                    message.success({ content: 'Processing cancelled', key });
                    trpcUtils.admin.marketPds.getMarketPds.invalidate({ id: pdsId });
                    trpcUtils.admin.marketPds.listMarketPds.invalidate();
                },
            },
        );
    };

    const handleLogoUpload = useCallback(
        async (file: RcFile) => {
            if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
                message.error('Please upload a PNG, JPG, SVG, or WebP image');
                return;
            }
            if (file.size > MAX_LOGO_SIZE) {
                message.error('Logo must be smaller than 5 MB');
                return;
            }

            setIsUploadingLogo(true);
            try {
                const { url, fields, key } = await trpcClient.admin.marketPds.getLogoUploadUrl.mutate({
                    filename: file.name,
                    contentType: file.type,
                });

                const formData = new FormData();
                for (const [k, v] of Object.entries(fields)) {
                    formData.append(k, v);
                }
                formData.append('file', file);

                await uploadToS3({ url, formData });

                form.setFieldValue('insurerLogo', key);
                // Invalidate so the logo URL resolves on next fetch
                trpcUtils.admin.marketPds.getMarketPds.invalidate({ id: pdsId });
                message.success('Logo uploaded');
            } catch (err) {
                errorMessage.show(err);
            } finally {
                setIsUploadingLogo(false);
            }
        },
        [trpcClient, form, trpcUtils, pdsId],
    );

    const handleLogoRemove = useCallback(() => {
        form.setFieldValue('insurerLogo', null);
    }, [form]);

    const categoryOptions = (categories ?? []).map((c) => ({ label: c, value: c }));
    const insurerOptions = (insurers ?? []).map((i) => ({ label: i.insurerName, value: i.insurerName }));

    const sections = marketPds?.Sections ?? [];

    if (isLoading) {
        return (
            <div className="space-y-6">
                <Skeleton active paragraph={{ rows: 8 }} />
            </div>
        );
    }

    return (
        <TRPCErrorBoundary error={error}>
            <ReadOnlyBanner module="comms" />

            <Form<EditFormValues>
                layout="vertical"
                form={form}
                onFinish={handleSave}
                id={formId}
                disabled={isReadOnly}
                className="space-y-6"
            >
                {/* Header actions */}
                <PageLayout.Header
                    rightAddon={
                        !isReadOnly && marketPds ? (
                            <Space>
                                {marketPds.status !== 'PROCESSING' && marketPds.fileKey && (
                                    <Button type="secondary" icon={<RefreshCw />} onClick={handleReprocess}>
                                        Reprocess
                                    </Button>
                                )}
                                <Button type="secondary" icon={<Replace />} onClick={() => setReplaceFileOpen(true)}>
                                    Replace file
                                </Button>
                            </Space>
                        ) : undefined
                    }
                    primaryActionAddon={
                        !isReadOnly ? (
                            <Button type="primary" htmlType="submit" form={formId} loading={isSaving}>
                                Save changes
                            </Button>
                        ) : undefined
                    }
                />

                {/* Processing alert with cancel button */}
                {marketPds?.status === 'PROCESSING' && (
                    <Alert
                        type="info"
                        showIcon
                        title="This PDS is currently being processed. The page will automatically refresh when processing completes."
                        action={
                            !isReadOnly ? (
                                <Popconfirm
                                    title="Cancel processing"
                                    description="Are you sure you want to cancel the current processing job?"
                                    onConfirm={handleCancelProcessing}
                                    okText="Yes, cancel"
                                    cancelText="No"
                                >
                                    <Button type="secondary" size="small" icon={<X />} loading={isCancelling}>
                                        Cancel processing
                                    </Button>
                                </Popconfirm>
                            ) : undefined
                        }
                    />
                )}

                {/* Processing error alert */}
                {marketPds?.processingError && (
                    <Alert type="error" showIcon title="Processing error" description={marketPds.processingError} />
                )}

                <Tabs
                    defaultActiveKey="details"
                    size="large"
                    items={[
                        {
                            key: 'details',
                            label: 'Details',
                            children: (
                                <div className="space-y-6">
                                    {/* Metadata */}
                                    <Card title="Metadata">
                                        <div className="grid gap-6 md:grid-cols-2">
                                            <Form.Item<EditFormValues>
                                                label="PDS name"
                                                name="name"
                                                rules={[{ required: true, message: 'Please enter a PDS name' }]}
                                                className="md:col-span-2"
                                            >
                                                <Input
                                                    maxLength={255}
                                                    placeholder="e.g., Business Insurance PDS 2024"
                                                />
                                            </Form.Item>

                                            <Form.Item<EditFormValues>
                                                label="Insurer name"
                                                name="insurerName"
                                                rules={[{ required: true, message: 'Please enter an insurer name' }]}
                                            >
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    placeholder="Select or type insurer name"
                                                    options={insurerOptions}
                                                    filterOption={(input, option) =>
                                                        (option?.label ?? '')
                                                            .toLowerCase()
                                                            .includes(input.toLowerCase())
                                                    }
                                                />
                                            </Form.Item>

                                            <Form.Item<EditFormValues> label="Insurer website" name="insurerWebsite">
                                                <Input placeholder="e.g., https://www.allianz.com.au" />
                                            </Form.Item>

                                            <div className="flex gap-4 md:col-span-2">
                                                <Form.Item<EditFormValues>
                                                    label="Category"
                                                    name="category"
                                                    rules={[{ required: true, message: 'Please enter a category' }]}
                                                    className="flex-1"
                                                >
                                                    <Select
                                                        showSearch
                                                        allowClear
                                                        placeholder="Select or type category"
                                                        options={categoryOptions}
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? '')
                                                                .toLowerCase()
                                                                .includes(input.toLowerCase())
                                                        }
                                                    />
                                                </Form.Item>

                                                <Form.Item<EditFormValues>
                                                    label="Year"
                                                    name="year"
                                                    rules={[{ required: true, message: 'Please enter a year' }]}
                                                    className="w-32"
                                                >
                                                    <InputNumber
                                                        min={2000}
                                                        max={2100}
                                                        placeholder="2024"
                                                        className="w-full"
                                                    />
                                                </Form.Item>

                                                <Form.Item<EditFormValues>
                                                    label="Version"
                                                    name="version"
                                                    className="w-32"
                                                >
                                                    <Input placeholder="e.g., v2.1" />
                                                </Form.Item>
                                            </div>

                                            <Form.Item<EditFormValues> label="Effective date" name="effectiveDate">
                                                <DatePicker className="w-full" />
                                            </Form.Item>

                                            <Form.Item<EditFormValues> label="Status" name="status">
                                                <Select options={STATUS_OPTIONS} />
                                            </Form.Item>

                                            {/* Insurer logo — hidden form field + visual component */}
                                            <Form.Item<EditFormValues> name="insurerLogo" hidden>
                                                <Input />
                                            </Form.Item>
                                            <div className="md:col-span-2">
                                                <label className="mb-2 block text-foreground text-sm">
                                                    Insurer logo
                                                </label>
                                                <InsurerLogoField
                                                    logoUrl={marketPds?.insurerLogoUrl ?? null}
                                                    isReadOnly={isReadOnly}
                                                    isUploading={isUploadingLogo}
                                                    onUpload={handleLogoUpload}
                                                    onRemove={handleLogoRemove}
                                                />
                                            </div>
                                        </div>
                                    </Card>

                                    {/* File information */}
                                    <Card title="File information">
                                        {marketPds?.fileKey ? (
                                            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                                                <Descriptions.Item label="File name">
                                                    {marketPds.fileName ?? '-'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Processing status">
                                                    <Tag
                                                        color={MARKET_PDS_STATUS_COLORS[marketPds.status] ?? 'default'}
                                                        variant="outlined"
                                                    >
                                                        {marketPds.status}
                                                    </Tag>
                                                </Descriptions.Item>
                                                {marketPds.processingError && (
                                                    <Descriptions.Item label="Processing error" span={2}>
                                                        <span className="text-status-danger">
                                                            {marketPds.processingError}
                                                        </span>
                                                    </Descriptions.Item>
                                                )}
                                                <Descriptions.Item label="Created">
                                                    {formatDateTime(marketPds.createdAt)}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Last updated">
                                                    {formatDateTime(marketPds.updatedAt)}
                                                </Descriptions.Item>
                                            </Descriptions>
                                        ) : (
                                            <p className="text-muted-foreground">No file uploaded</p>
                                        )}
                                    </Card>
                                </div>
                            ),
                        },
                        {
                            key: 'analysis',
                            label: 'AI analysis',
                            children: <PdsAnalysisTab processedData={marketPds?.processedData} />,
                        },
                        {
                            key: 'sections',
                            label: `Sections (${sections.length})`,
                            children: (
                                <Table<MarketPdsSection>
                                    dataSource={sections}
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    locale={{ emptyText: 'No sections — upload and process a PDF to extract sections' }}
                                    expandable={{
                                        expandedRowRender: (record) => (
                                            <p className="whitespace-pre-wrap text-muted-foreground text-sm">
                                                {record.content}
                                            </p>
                                        ),
                                    }}
                                    columns={[
                                        {
                                            title: 'Type',
                                            dataIndex: 'type',
                                            key: 'type',
                                            width: 120,
                                            render: (type: string) => (
                                                <Tag color={SECTION_TYPE_COLORS[type] ?? 'default'} variant="outlined">
                                                    {type}
                                                </Tag>
                                            ),
                                        },
                                        {
                                            title: 'Title',
                                            dataIndex: 'title',
                                            key: 'title',
                                            ellipsis: true,
                                        },
                                        {
                                            title: 'Subcategory',
                                            dataIndex: 'subcategory',
                                            key: 'subcategory',
                                            ellipsis: true,
                                            render: (value: string | null) => value ?? '-',
                                        },
                                        {
                                            title: 'Summary',
                                            dataIndex: 'summary',
                                            key: 'summary',
                                            ellipsis: true,
                                            width: 300,
                                        },
                                        {
                                            title: 'Key terms',
                                            dataIndex: 'keyTerms',
                                            key: 'keyTerms',
                                            width: 200,
                                            render: (terms: string[]) =>
                                                terms.length > 0 ? (
                                                    <Space size="micro" wrap>
                                                        {terms.slice(0, 3).map((term) => (
                                                            <Tag key={term} variant="outlined">
                                                                {term}
                                                            </Tag>
                                                        ))}
                                                        {terms.length > 3 && (
                                                            <span className="text-muted-foreground text-xs">
                                                                +{terms.length - 3}
                                                            </span>
                                                        )}
                                                    </Space>
                                                ) : (
                                                    '-'
                                                ),
                                        },
                                    ]}
                                />
                            ),
                        },
                        {
                            key: 'raw-text',
                            label: 'Raw text',
                            children: marketPds?.rawText ? (
                                <div className="max-h-[70vh] overflow-auto rounded border border-separator bg-card p-4">
                                    <Markdown variant="document">{marketPds.rawText}</Markdown>
                                </div>
                            ) : (
                                <Empty description="No raw text available. Process a PDF to extract text." />
                            ),
                        },
                    ]}
                />
            </Form>

            {/* Replace file modal */}
            {marketPds && (
                <SmartPdsUploadModal
                    open={replaceFileOpen}
                    onClose={() => setReplaceFileOpen(false)}
                    existingPdsId={marketPds.id}
                    onSuccess={() => {
                        setReplaceFileOpen(false);
                        trpcUtils.admin.marketPds.getMarketPds.invalidate({ id: pdsId });
                        trpcUtils.admin.marketPds.listMarketPds.invalidate();
                        trpcUtils.admin.marketPds.listCategories.invalidate();
                        trpcUtils.admin.marketPds.listInsurers.invalidate();
                    }}
                    mode="admin"
                />
            )}
        </TRPCErrorBoundary>
    );
}
